import { API_BASE_URL } from '../config/api.config';
import { sessionExpiry } from './session-expiry.service';

/**
 * HTTP client for the niup-me-backend.
 *
 * Authentication is cookie-based (HttpOnly session cookie set by the backend
 * after OAuth2 login). No Bearer token is injected here — `credentials: 'include'`
 * ensures the browser sends the cookie automatically.
 *
 * On 401 the SPA redirects to the backend's OAuth2 login endpoint.
 */
class ApiService {
  private baseUrl = API_BASE_URL;

  /** Active UI locale, sent as Accept-Language so backend content
   *  (e.g. legal pages) matches the UI language. Set via setLocale(). */
  private locale: string | null = null;

  /** Set the locale sent with every request. Called by the locale store. */
  setLocale(locale: string): void {
    this.locale = locale;
  }

  /** Build common headers for a request (locale + optional JSON content type). */
  private buildHeaders(hasJsonBody: boolean): Record<string, string> {
    const headers: Record<string, string> = {};
    if (this.locale) {
      headers['Accept-Language'] = this.locale;
    }
    if (hasJsonBody) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers = this.buildHeaders(body != null);

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    if (response.status === 401) {
      sessionExpiry.handleSessionExpired();
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const msg = body?.error?.message || '';
      if (response.status === 403 && (msg.includes('CSRF') || msg.includes('session'))) {
        sessionExpiry.handleSessionExpired();
        throw new Error('Unauthorized');
      }
      throw new Error(msg || 'Request failed');
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  /** Like get(), but throws on 401/403 instead of redirecting to login. */
  async getSafe<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      headers: this.buildHeaders(false),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(response.status === 401 || response.status === 403 ? 'Forbidden' : 'Request failed');
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  async uploadFile<T>(path: string, formData: FormData): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'PUT',
      headers: this.buildHeaders(false),
      body: formData,
      credentials: 'include',
    });

    if (response.status === 401) {
      sessionExpiry.handleSessionExpired();
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const msg = body?.error?.message || '';
      if (response.status === 403 && (msg.includes('CSRF') || msg.includes('session'))) {
        sessionExpiry.handleSessionExpired();
        throw new Error('Unauthorized');
      }
      throw new Error(msg || 'Upload failed');
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }
}

export const apiService = new ApiService();
